import { AuthGoogleIcon } from './auth-google-icon';
import {
  AuthFormHeading,
  AUTH_FORM_SPACING,
  AUTH_PRIMARY_BUTTON,
  AUTH_SECONDARY_BUTTON,
  AuthAlternativeActions,
  AuthFormError,
} from './auth-form-presentation';
import { ButtonLoader } from '@/components/loaders';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import type { useSignInController } from './use-sign-in-controller';
export function SignInCredentialsView(
  model: ReturnType<typeof useSignInController>
) {
  const {
    onToggleMode,
    onPasswordReset,
    isLikelyEmail,
    copy,
    isLoading,
    onCredentialInput,
    pendingMethod,
    deactivationNotice,
    form,
    displayedError,
    handleSubmit,
    handleGoogleSignIn,
  } = model;
  // Show sign-in form
  return (
    <div className="w-full min-w-0">
      <div>
        <AuthFormHeading
          title={<>{copy.welcomeBack}</>}
          description={<>{copy.letsGetYouBackIn}</>}
        />
      </div>

      {deactivationNotice === 'requested' ? (
        <div className="mb-4 rounded-md border border-amber-500/20 bg-amber-500/10 p-3 text-start text-sm text-amber-950">
          {copy.deactivationRequestedNotice}
        </div>
      ) : null}

      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(handleSubmit)}
          className={AUTH_FORM_SPACING}
          autoComplete="on"
          aria-busy={isLoading}
        >
          <div className={AUTH_FORM_SPACING}>
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem className="gap-1.5">
                  <FormLabel>{copy.email}</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      type="email"
                      name="email"
                      placeholder={copy.emailPlaceholder}
                      autoComplete="email"
                      spellCheck={false}
                      disabled={isLoading}
                      className="rounded-sm! text-base"
                      onChange={(event) => {
                        field.onChange(event);
                        onCredentialInput();
                      }}
                      onBlur={(event) => {
                        field.onBlur();
                        const nextEmail = event.target.value
                          .trim()
                          .toLowerCase();
                        if (!nextEmail) {
                          field.onChange('');
                          return;
                        }
                        if (
                          isLikelyEmail(nextEmail) &&
                          nextEmail !== event.target.value
                        ) {
                          field.onChange(nextEmail);
                        }
                      }}
                    />
                  </FormControl>
                  <FormMessage role="alert" />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem className="gap-3">
                  <FormLabel>{copy.password}</FormLabel>
                  <FormControl>
                    <PasswordInput
                      {...field}
                      name="password"
                      placeholder={copy.password}
                      autoComplete="current-password"
                      disabled={isLoading}
                      className="text-base"
                      groupClassName="rounded-sm!"
                      toggleClassName="text-muted-foreground hover:text-foreground"
                      onChange={(event) => {
                        field.onChange(event);
                        onCredentialInput();
                      }}
                    />
                  </FormControl>

                  <FormMessage role="alert" />
                </FormItem>
              )}
            />
          </div>

          <AuthFormError>{displayedError}</AuthFormError>

          <div className="pt-[clamp(0.5rem,1svh,2rem)]">
            <Button
              type="submit"
              className={AUTH_PRIMARY_BUTTON}
              disabled={isLoading}
            >
              <ButtonLoader
                isLoading={pendingMethod === 'credentials'}
                loadingText={copy.signingIn}
              >
                {copy.signIn}
              </ButtonLoader>
            </Button>
          </div>
          <AuthAlternativeActions>
            <Button
              type="button"
              variant="outline"
              className={AUTH_SECONDARY_BUTTON}
              disabled={isLoading}
              onClick={handleGoogleSignIn}
            >
              {pendingMethod !== 'google' ? <AuthGoogleIcon /> : null}
              <ButtonLoader
                isLoading={pendingMethod === 'google'}
                loadingText={copy.signingIn}
              >
                {copy.continueWithGoogle}
              </ButtonLoader>
            </Button>

            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={onPasswordReset}
              className={AUTH_SECONDARY_BUTTON}
              disabled={isLoading}
            >
              {copy.forgotPassword}
            </Button>
          </AuthAlternativeActions>

          <div className="text-center text-sm">
            {copy.dontHaveAccount}{' '}
            <button
              type="button"
              onClick={onToggleMode}
              className="text-primary inline-flex min-h-8 cursor-pointer items-center font-medium underline-offset-4 hover:underline focus-visible:outline-2 focus-visible:outline-offset-2"
              disabled={isLoading}
            >
              {copy.signUp}
            </button>
          </div>
        </form>
      </Form>
    </div>
  );
}
